import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  User, AtSign, ArrowLeft, Sparkles, Briefcase, Star, Mic, Trophy, Lock, Award,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAT_DEFS = [
  { key: "optimizations", label: "Optimizations", icon: Sparkles, color: "#6366f1" },
  { key: "applications", label: "Applications", icon: Briefcase, color: "#3b82f6" },
  { key: "shortlisted", label: "Shortlisted", icon: Star, color: "#f59e0b" },
  { key: "interviews", label: "Interviews", icon: Mic, color: "#8b5cf6" },
  { key: "offers", label: "Offers", icon: Trophy, color: "#10b981" },
];

export default function PublicProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true); setNotFound(false);
    axios.get(`${API}/social/profile/${encodeURIComponent(username)}`)
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [username]);

  if (loading) {
    return <div className="analytics-loading" data-testid="public-profile-loading"><div className="processing-spinner" /></div>;
  }
  if (notFound) {
    return (
      <div className="page-container fade-in" data-testid="public-profile-notfound">
        <button className="btn-secondary" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>
        <div className="card" style={{ padding: "2rem", textAlign: "center", marginTop: "1rem" }}>
          <User size={40} style={{ opacity: 0.4 }} />
          <h2>Profile not found</h2>
          <p style={{ color: "var(--text-secondary)" }}>No user with username “{username}”.</p>
        </div>
      </div>
    );
  }

  const p = data.profile;
  const stats = data.stats;
  const isPrivate = p.private;

  return (
    <div className="page-container fade-in" data-testid="public-profile-page">
      <button className="btn-secondary" data-testid="public-back-btn" onClick={() => navigate(-1)}>
        <ArrowLeft size={16} /> Back
      </button>

      <div className="card" style={{ padding: "1.8rem", marginTop: "1rem", display: "flex", gap: "1.2rem", alignItems: "center" }} data-testid="public-profile-header">
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "var(--border-main)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {p.avatar_url ? <img src={p.avatar_url} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} /> : <User size={28} />}
        </div>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: "1.5rem" }} data-testid="public-name">{p.name || p.username}</h1>
          <div style={{ color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 3 }}>
            <AtSign size={14} />{p.username}
          </div>
          {p.headline && <div style={{ marginTop: 4 }}>{p.headline}</div>}
          {p.recruiter_placements > 0 && (
            <div data-testid="public-recruiter-credit" style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.8rem", padding: "3px 10px", borderRadius: 999, background: "rgba(16,185,129,0.15)", color: "#10b981", fontWeight: 600 }}>
              <Award size={13} /> Helped land {p.recruiter_placements} job{p.recruiter_placements !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      </div>

      {p.bio && !isPrivate && (
        <div className="card" style={{ padding: "1.2rem", marginTop: "1rem" }} data-testid="public-bio">{p.bio}</div>
      )}

      {isPrivate ? (
        <div className="card" style={{ padding: "2rem", textAlign: "center", marginTop: "1rem" }} data-testid="public-private">
          <Lock size={32} style={{ opacity: 0.4 }} />
          <p style={{ color: "var(--text-secondary)" }}>This profile is private.</p>
        </div>
      ) : (
        <div data-testid="public-stats" style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
          gap: "0.9rem", marginTop: "1rem",
        }}>
          {STAT_DEFS.map(({ key, label, icon: Icon, color }) => (
            stats?.[key] === null ? null : (
              <div key={key} className="card" data-testid={`public-stat-${key}`}
                style={{ padding: "1.1rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color }}>
                  <Icon size={18} /><span style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>{label}</span>
                </div>
                <span style={{ fontSize: "1.9rem", fontWeight: 700 }}>{stats?.[key] ?? 0}</span>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}
